import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { AssignmentType, Contragent, Option, SortOrder } from "tm-shared/types";

import { QueueTaskStatus } from "@/hooks/core/useAsyncTaskQueue";
import { FilterInputValue } from "@/types/common";
import {
    AddressVerifyModalSelectionPayload,
    DuplicateResolution,
    NewPointFormValues,
    PointCreateModalSelectionPayload,
} from "@/types/form";
import { ModalState, ModalType, PointCreateModalState } from "@/types/modals";
import { RequestQueueItem } from "@/types/requestQueue";

import { getCheckoutsThunk } from "./ui.slice.thunks";
import { setMdTasksConfigProps, SetTaskFieldProps, UiState } from "./ui.types";

const initialState: UiState = {
    mdTasksConfig: {},

    assignments: {
        page: {
            filter: null,
            activeCollapseId: null,
        },
    },

    updateProductBalances: {
        query: "",
    },

    rejectProducts: {
        queryMap: {},
        selectedFilters: {},
    },

    saleAdditional: {
        query: "",
    },

    workPlan: {
        query: "",
        pointsSortOption: { value: "route", label: "по маршруту" },
        activeCollapse: null,
    },

    checkout: {
        activeCollapse: null,
        loading: false,
    },

    modalsQueueList: [],
    lastDataRefreshAt: null,
    failedQueue: [],

    pointCreatePage: {
        newPointFormValues: null,
        modal: null,
        modalSelectionPayload: null,
        duplicateResolution: null,
        optionCache: {},
        contragentsMap: {},
    },
    addressVerifyModalSelectionPayload: null,
    addressVerifyDuplicateResolution: null,

    productsFilter: {
        selectedFilters: {},
        sort: {
            tag: null,
            order: SortOrder.Asc,
        },
        hideZeroProducts: false,
        showOnlyMML: false,
    },

    initFailedRequests: [],

    asyncStatus: {
        paymentExportPending: false,
    },
};

export const UiSlice = createSlice({
    name: "ui",
    initialState,
    reducers: {
        resetUiState: () => initialState,
        // ----------------------- MD ---------------------------
        setMdTasksConfig: (
            state,
            { payload }: PayloadAction<setMdTasksConfigProps>,
        ) => {
            const { pointId, taskId } = payload;
            const pointConfig = (state.mdTasksConfig[pointId] ??= {});
            const taskConfig = (pointConfig[taskId] ??= {});

            if ("subtaskId" in payload && payload.subtaskId) {
                const subtasks = (taskConfig.subtasks ??= {});
                subtasks[payload.subtaskId] = {
                    ...subtasks[payload.subtaskId],
                    [payload.field ?? "activeItem"]: payload.value,
                };
            } else {
                const { field, value } = payload as SetTaskFieldProps;
                pointConfig[taskId] = {
                    ...taskConfig,
                    [field ?? "activeItem"]: value,
                };
            }
        },

        // ---------------------------- Assignments --------------

        setAssignmentActiveCollapseId: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.assignments.page.activeCollapseId = action.payload;
        },

        setAssignmentsFilter: (
            state,
            action: PayloadAction<AssignmentType | null>,
        ) => {
            state.assignments.page.filter = action.payload;
        },

        // ---------------------------- Sale additional --------------

        setSaleQuery: (state, { payload }: PayloadAction<string>) => {
            state.saleAdditional.query = payload;
        },

        // ---------------------------- Reject Products --------------

        setRejectProductsSelectedFilters: (
            state,
            {
                payload,
            }: PayloadAction<{
                [tag: string]: (string | number | boolean)[];
            } | null>,
        ) => {
            if (payload === null) {
                state.rejectProducts.selectedFilters = {};
                return;
            }

            for (const [key, values] of Object.entries(payload)) {
                if (values.length === 0) {
                    delete state.rejectProducts.selectedFilters[key];

                    continue;
                }

                state.rejectProducts.selectedFilters[key] = values;
            }
        },

        setRejectProductsQuery: (
            state,
            { payload }: PayloadAction<{ taskId: string; query: string }>,
        ) => {
            state.rejectProducts.queryMap = {
                ...state.rejectProducts.queryMap,
                [payload.taskId]: payload.query,
            };
        },

        // ---------------------------- Update Product Balances --------------

        setUpdateProductBalancesQuery: (
            state,
            action: PayloadAction<string>,
        ) => {
            state.updateProductBalances.query = action.payload;
        },

        // ---------------------------- Work plan --------------

        setWorkPlanQuery: (state, action: PayloadAction<string>) => {
            state.workPlan.query = action.payload;
        },

        setWorkPlanActiveCollapse: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.workPlan.activeCollapse = action.payload;
        },

        setPointsSortOption: (
            state,
            {
                payload,
            }: PayloadAction<{
                value: string;
                label: string;
            }>,
        ) => {
            state.workPlan.pointsSortOption = payload;
        },

        // ---------------------------- Checkout --------------
        setCheckoutActiveCollapse: (
            state,
            action: PayloadAction<string | null>,
        ) => {
            state.checkout.activeCollapse = action.payload;
        },

        // ---------------------------- Modals queue --------------

        addModalToQueue: (state, action: PayloadAction<ModalState>) => {
            if (state.modalsQueueList.some((m) => m.id === action.payload.id))
                return;

            state.modalsQueueList.push(action.payload);
        },
        removeModalFromQueue: (state, { payload }: PayloadAction<string>) => {
            state.modalsQueueList = state.modalsQueueList.filter(
                (m) => m.id !== payload,
            );
        },
        clearReminderModals: (state) => {
            if (!state?.modalsQueueList.length) return;
            state.modalsQueueList = state.modalsQueueList.filter(
                (m) =>
                    m.type !== ModalType.UncompletedTasks &&
                    m.type !== ModalType.UncompletedCheckouts,
            );
        },

        // ---------------------------- Global cooldown--------------

        setLastDataRefreshAt(state, action: PayloadAction<number | null>) {
            state.lastDataRefreshAt = action.payload;
        },

        // -------------------- Failed Queue -------------------

        addFailedQueueItem: (
            state,
            action: PayloadAction<RequestQueueItem>,
        ) => {
            state.failedQueue.push({
                ...action.payload,
                status: QueueTaskStatus.Error,
            });
        },

        // ----------------------------- Create Point Page -----------------------------

        setNewPointForm: (state, action: PayloadAction<NewPointFormValues>) => {
            state.pointCreatePage.newPointFormValues = action.payload;
        },

        setPointCreatePageModal: (
            state,
            action: PayloadAction<PointCreateModalState | null>,
        ) => {
            state.pointCreatePage.modal = action.payload;
        },

        setPointCreatePageModalSelectionPayload: (
            state,
            action: PayloadAction<PointCreateModalSelectionPayload | null>,
        ) => {
            state.pointCreatePage.modalSelectionPayload = action.payload;
        },

        setPointCreatePageDuplicateResolution: (
            state,
            action: PayloadAction<DuplicateResolution | null>,
        ) => {
            state.pointCreatePage.duplicateResolution = action.payload;
        },

        setPointCreatePageOptionsCache(
            state,
            action: PayloadAction<{
                fieldName: keyof NewPointFormValues;
                options: Option[];
            }>,
        ) {
            const { fieldName, options } = action.payload;

            state.pointCreatePage.optionCache[fieldName] = options;
        },

        clearPointCreatePageCache(state) {
            state.pointCreatePage.optionCache = {};
            state.pointCreatePage.contragentsMap = {};
        },

        setPointCreatePageContragentsMap(
            state,
            action: PayloadAction<Record<string, Contragent>>,
        ) {
            state.pointCreatePage.contragentsMap = action.payload;
        },

        setAddressVerifyModalSelectionPayload: (
            state,
            action: PayloadAction<AddressVerifyModalSelectionPayload | null>,
        ) => {
            state.addressVerifyModalSelectionPayload = action.payload;
        },

        setAddressVerifyDuplicateResolution: (
            state,
            action: PayloadAction<DuplicateResolution | null>,
        ) => {
            state.addressVerifyDuplicateResolution = action.payload;
        },

        // ----------------------------- Products filter -----------------------------

        patchProductsSelectedFilters(
            state,
            action: PayloadAction<Record<string, FilterInputValue>>,
        ) {
            if (Object.keys(action.payload).length === 0) {
                state.productsFilter.selectedFilters = {};
            } else {
                state.productsFilter.selectedFilters = {
                    ...state.productsFilter.selectedFilters,
                    ...action.payload,
                };
            }
        },
        setProductsSelectedFilters(
            state,
            action: PayloadAction<Record<string, FilterInputValue>>,
        ) {
            state.productsFilter.selectedFilters = action.payload;
        },
        setProductsHideZero(state, action: PayloadAction<boolean>) {
            state.productsFilter.hideZeroProducts = action.payload;
        },
        setProductsShowOnlyMML(state, action: PayloadAction<boolean>) {
            state.productsFilter.showOnlyMML = action.payload;
        },
        setProductsSortTag: (state, action: PayloadAction<string | null>) => {
            state.productsFilter.sort.tag = action.payload;
        },
        setProductsSortOrder: (state, action: PayloadAction<SortOrder>) => {
            state.productsFilter.sort.order = action.payload;
        },

        // ----------------------------- initFailedRequests -----------------------------

        setInitFailedRequests: (state, action: PayloadAction<string[]>) => {
            state.initFailedRequests = action.payload;
        },

        setAsyncStatus: (
            state,
            action: PayloadAction<{
                key: keyof UiState["asyncStatus"];
                value: boolean;
            }>,
        ) => {
            const { key, value } = action.payload;

            state.asyncStatus[key] = value;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getCheckoutsThunk.pending, (state) => {
                state.checkout.loading = true;
            })
            .addMatcher(
                isAnyOf(
                    getCheckoutsThunk.fulfilled,
                    getCheckoutsThunk.rejected,
                ),
                (state) => {
                    state.checkout.loading = false;
                },
            );
    },
});

export const {
    resetUiState,
    setMdTasksConfig,
    setAssignmentActiveCollapseId,

    // ---------------------------- Assignments --------------
    setAssignmentsFilter,

    // ---------------------------- Sale Page --------------
    setSaleQuery,

    // ---------------------------- Reject Page --------------
    setRejectProductsQuery,
    setRejectProductsSelectedFilters,

    // ---------------------------- Update Product Balances --------------
    setUpdateProductBalancesQuery,

    // ---------------------------- WorkPlan --------------
    setWorkPlanQuery,
    setWorkPlanActiveCollapse,
    setPointsSortOption,

    // ---------------------------- Checkout --------------
    setCheckoutActiveCollapse,

    // ---------------------------- Modals queue --------------
    addModalToQueue,
    removeModalFromQueue,
    clearReminderModals,

    // ---------------------------- Global cooldown --------------
    setLastDataRefreshAt,

    // ---------------------------- FailedQueue --------------
    addFailedQueueItem,

    // ---------------------------- Create Point Page --------------
    clearPointCreatePageCache,
    setPointCreatePageModalSelectionPayload,
    setPointCreatePageDuplicateResolution,
    setPointCreatePageOptionsCache,
    setNewPointForm,
    setPointCreatePageModal,
    setPointCreatePageContragentsMap,

    // ---------------------------- Address Verify --------------
    setAddressVerifyModalSelectionPayload,
    setAddressVerifyDuplicateResolution,

    // ---------------------------- Products filter --------------
    patchProductsSelectedFilters,
    setProductsHideZero,
    setProductsShowOnlyMML,
    setProductsSortTag,
    setProductsSortOrder,
    setProductsSelectedFilters,

    // ---------------------------- initFailedRequests --------------
    setInitFailedRequests,

    // ---------------------------- asyncStatus --------------
    setAsyncStatus,
} = UiSlice.actions;

export const uiReducer = UiSlice.reducer;
